/**
 * Sports Market Component
 * Unified component for displaying sports markets (works for both desktop and mobile)
 * Shows matches with BACK/LAY betting options
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBetting } from '@/Context/BettingContext';
import { useScreenSize } from '@/hooks/useScreenSize';
import { useSportsMarkets } from '@/hooks/useSportsMarkets';
import { BET_TYPES } from '@/utils/constants';
import { ErrorStatsvgIcon } from '@/components/icons';
import styles from './SportsMarket.module.css';

const SportsMarket = ({ sportId, upperTab = 'in-play', sport = null }) => {
  const { addBet } = useBetting();
  const { isMobile } = useScreenSize();
  const navigate = useNavigate();
  const [selectedCell, setSelectedCell] = useState(null);

  // Fetch matches using custom hook with React Query
  // Use isFetching to detect when data is being fetched (including refetches)
  const { data: matchesData, isLoading, isFetching, error } = useSportsMarkets(sportId, upperTab);

  // Helper function to get sport icon based on sport name or ID
  const getSportIcon = (matchSportName, matchSportId) => {
    const sportName = matchSportName?.toLowerCase() || '';
    const sportId = matchSportId?.toString() || '';
    
    // Map sport names/IDs to icon paths
    if (sportName.includes('cricket') || sportId === '4') {
      return '/assets/images/sports/cricket.png';
    } else if (sportName.includes('soccer') || sportName.includes('football') || sportId === '1') {
      return '/assets/images/sports/soccer.png';
    } else if (sportName.includes('tennis') || sportId === '2') {
      return '/assets/images/sports/tennis.png';
    }
    // Default to soccer if no match
    return '/assets/images/sports/soccer.png';
  };

  // Transform matches data to market format for display
  const marketData = useMemo(() => {
    if (!matchesData || matchesData.length === 0) return [];

    // Group by sport if showing all sports
    const grouped = !sportId 
      ? matchesData.reduce((acc, match) => {
          const sport = match.sportName || 'Other';
          if (!acc[sport]) acc[sport] = [];
          acc[sport].push(match);
          return acc;
        }, {})
      : { [sportId]: matchesData };

    // Convert to array format
    const result = [];
    Object.entries(grouped).forEach(([sportKey, matches]) => {
      result.push({
        sportName: matches[0]?.sportName || sportKey,
        matches: matches.map((match) => ({
          matchId: match.id,
          matchTitle: match.matchTitle,
          team1: match.team1,
          team2: match.team2,
          team3: match.team3,
          eventid: match.eventid,
          isLive: match.isLive,
          status: match.status,
          date: match.date,
          time: match.time,
          totalMatched: match.odds?.totalMatched || 0,
          odds: match.odds,
          isSuspended: match.isSuspended || false,
          sportName: match.sportName,
          sportId: match.sportId,
        })),
      });
    });

    return result;
  }, [matchesData, sportId]);

  const handleMatchClick = (match) => {
    const matchId = match.matchId || match.id;
    if (!matchId) return;

    navigate(`/sports/markets/${matchId}`, {
      state: {
        matchData: {
          id: matchId,
          matchId: matchId,
          eventid: match.eventid || matchId,
          eventId: match.eventid || matchId,
          team1: match.team1,
          team2: match.team2,
          matchTitle: match.matchTitle || `${match.team1} v ${match.team2}`,
          status: match.status,
          isLive: match.isLive,
          isSuspended: match.isSuspended || false,
          odds: match.odds,
          date: match.date,
          time: match.time,
          sportName: match.sportName,
          sportId: match.sportId,
        }
      }
    });
  };

  const handleOddsClick = (match, category, type, odds, e) => {
    if (e) {
      e.stopPropagation();
    }
    if (match.isSuspended || odds === '--' || odds === 'Suspend') return;

    let selectionName = '';
    if (category === 'matched' || category === '1') {
      selectionName = match.team1;
    } else if (category === 'x') {
      selectionName = 'The Draw';
    } else if (category === 'two' || category === '2') {
      selectionName = match.team2;
    }

    const betDetails = {
      matchId: match.matchId,
      matchTitle: match.matchTitle,
      team1: match.team1,
      team2: match.team2,
      selectionName,
      category,
      type,
      odds,
      isBlue: type === 'back',
      stake: '',
      liability: '0.00',
      market: 'MO',
      marketName: 'Match Odds',
      eventid: match.eventid,
    };

    addBet(betDetails);
  };

  const formatDate = (date) => {
    const now = new Date();
    const matchDate = new Date(date);
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const matchDay = new Date(matchDate.getFullYear(), matchDate.getMonth(), matchDate.getDate());
    
    if (matchDay.getTime() === today.getTime()) {
      return `Today ${matchDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
    }
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[matchDate.getDay()]} ${matchDate.getDate()} ${months[matchDate.getMonth()]} ${matchDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
  };

  const formatCurrency = (amount) => {
    if (!amount || amount === 0) return '0';
    return Math.round(amount).toLocaleString();
  };

  // Check if we have valid data
  const hasData = matchesData && Array.isArray(matchesData) && matchesData.length > 0;
  const hasMarketData = marketData && marketData.length > 0;
  
  // Only show loading on initial load (when no data exists yet)
  // Don't show loading during background refetches to avoid flickering
  if (isLoading && !hasData) {
    return (
      <div className={styles.sportsMarket}>
        <div className={styles.loadingContainer}>
          <div className={styles.loader}>
            <span className={styles.loaderText}>loading</span>
            <span className={styles.load}></span>
          </div>
        </div>
      </div>
    );
  }

  // If we have data, show it (even if background refetch is happening)
  // This ensures data appears as soon as it's available
  if (hasMarketData) {
    // Continue to render matches below
  } else if (!isLoading && !isFetching) {
    // Only show empty state when we're sure we're done loading
    return (
      <div className={styles.sportsMarket}>
        <div className={styles.emptyState}>
          <ErrorStatsvgIcon size="152" className={styles.emptyBetsIcon} />
          <p className={styles.emptyStateText}>No matches available</p>
        </div>
      </div>
    );
  } else {
    // Still loading - wait (should be caught by first if, but just in case)
    return null;
  }

  // Mobile view - simplified list for all tabs (only match names)
  if (isMobile) {
    return (
      <div className={styles.sportsMarket}>
        <div className={styles.mobileSimpleList}>
          {marketData.flatMap((sportGroup) => 
            sportGroup.matches.map((match) => (
              <div key={match.matchId} className={styles.mobileMatchName} onClick={() => handleMatchClick(match)}>
                <div className={styles.titleContainer}>
                  {match.isLive ? (
                    <span className={styles.liveDot}></span>
                  ) : (
                    <span className={styles.noLiveDot}></span>
                  )}
                  <img 
                    src={getSportIcon(match.sportName, match.sportId)} 
                    alt={match.sportName || 'sport'} 
                    className={styles.sportSticker}
                  />
                  <div className={styles.matchTitle}>{`${match.team1} v ${match.team2}`}</div>
                </div>
                <div className={styles.mobileMatchMeta}>
                  {match.isLive ? (
                    <span className={styles.status}>In-Play</span>
                  ) : (
                    match.status && <span className={styles.noStatus}>{match.status}</span>
                  )}
                  <div className={styles.iconGroup}>
                    <img 
                      src='/assets/images/sports/livestream.png' 
                      style={{ width: "15px", height: "15px", objectFit: 'contain', cursor: 'pointer' }} 
                      alt="live" 
                      onClick={(e) => {
                        e.stopPropagation();
                        // Handle livestream click
                      }}
                    />
                    <img 
                      src='/assets/images/sports/watch.png' 
                      style={{ width: "15px", height: "15px", objectFit: 'contain', cursor: 'pointer' }} 
                      alt="watch" 
                      onClick={(e) => {
                        e.stopPropagation();
                        // Handle watch click
                      }}
                    />
                    <img 
                      src='/assets/images/sports/fancy.png' 
                      style={{ width: "15px", height: "15px", objectFit: 'contain', cursor: 'pointer' }} 
                      alt="fancy" 
                      onClick={(e) => {
                        e.stopPropagation();
                        // Handle fancy click
                      }}
                    />
                    <img 
                      src='/assets/images/sports/book.png' 
                      style={{ width: "15px", height: "15px", objectFit: 'contain', cursor: 'pointer' }} 
                      alt="book" 
                      onClick={(e) => {
                        e.stopPropagation();
                        // Handle book click
                      }}
                    />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  // Desktop view or mobile "in-play" - full table layout (matching old code style)
  return (
    <div className={styles.sportsMarket}>
      <div className={styles.tableContainer}>
        {/* Sticky Header Row */}
        <div className={styles.stickyHeader}>
          <div className={styles.headerRow}>
            <div className={styles.headerGamesCol}>Games</div>
            <div className={styles.headerMatchedCol}>Matched PTE</div>
            <div className={styles.headerOutcomeCol}>1</div>
            <div className={styles.headerOutcomeCol}>X</div>
            <div className={styles.headerOutcomeCol}>2</div>
          </div>
        </div>

        {/* Matches List */}
        <div className={styles.matchesList}>
          {marketData.flatMap((sportGroup) => 
            sportGroup.matches.map((match) => (
              <div key={match.matchId} className={styles.matchRow}>
                <div className={styles.matchInfo} onClick={() => handleMatchClick(match)}>
                  <div className={styles.titleContainer}>
                    {match.isLive && (
                      <span className={styles.liveDot}></span>
                    )}
                    <img 
                      src={getSportIcon(match.sportName, match.sportId)} 
                      alt={match.sportName || 'sport'} 
                      className={styles.sportSticker}
                    />
                    <div className={styles.matchTitle}>{`${match.team1} v ${match.team2}`}</div>
                  </div>
                  <div className={styles.matchMeta}>
                    {match.isLive ? (
                      <span className={styles.status}>In-Play</span>
                    ) : (
                      <span className={styles.noStatus}>{match.status}</span>
                    )}
                    <div className={styles.iconGroup}>
                      <img 
                        src='/assets/images/sports/livestream.png' 
                        style={{ width: "13px", height: "13px", objectFit: 'contain', cursor: 'pointer' }} 
                        alt="live" 
                        onClick={(e) => {
                          e.stopPropagation();
                          // Handle livestream click
                        }}
                      />
                      <img 
                        src='/assets/images/sports/watch.png' 
                        style={{ width: "13px", height: "13px", objectFit: 'contain', cursor: 'pointer' }} 
                        alt="watch" 
                        onClick={(e) => {
                          e.stopPropagation();
                          // Handle watch click
                        }}
                      />
                      <img 
                        src='/assets/images/sports/fancy.png' 
                        style={{ width: "13px", height: "13px", objectFit: 'contain', cursor: 'pointer' }} 
                        alt="fancy" 
                        onClick={(e) => {
                          e.stopPropagation();
                          // Handle fancy click
                        }}
                      />
                      <img 
                        src='/assets/images/sports/book.png' 
                        style={{ width: "13px", height: "13px", objectFit: 'contain', cursor: 'pointer' }} 
                        alt="book" 
                        onClick={(e) => {
                          e.stopPropagation();
                          // Handle book click
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className={styles.pte}>{formatCurrency(match.totalMatched)}</div>
                <div className={styles.oddsContainer}>
                  {/* Outcome 1 */}
                  <div className={styles.oddsSection}>
                    <div
                      className={`${styles.oddsBox} ${styles.blue} ${match.isSuspended ? styles.suspended : ''}`}
                      onClick={(e) => handleOddsClick(match, 'matched', 'back', match.odds?.one?.back?.price || '--', e)}
                    >
                      {match.odds?.one?.back?.price || '--'}
                    </div>
                    <div
                      className={`${styles.oddsBox} ${match.isSuspended ? styles.suspended : ''}`}
                      onClick={(e) => handleOddsClick(match, 'matched', 'lay', match.odds?.one?.lay?.price || '--', e)}
                    >
                      {match.odds?.one?.lay?.price || '--'}
                    </div>
                  </div>
                  {/* Outcome X */}
                  {match.odds?.x && (
                    <div className={styles.oddsSection}>
                      <div
                        className={`${styles.oddsBox} ${styles.blue} ${match.isSuspended ? styles.suspended : ''}`}
                        onClick={(e) => handleOddsClick(match, 'x', 'back', match.odds.x.back.price || '--', e)}
                      >
                        {match.odds.x.back.price || '--'}
                      </div>
                      <div
                        className={`${styles.oddsBox} ${match.isSuspended ? styles.suspended : ''}`}
                        onClick={(e) => handleOddsClick(match, 'x', 'lay', match.odds.x.lay.price || '--', e)}
                      >
                        {match.odds.x.lay.price || '--'}
                      </div>
                    </div>
                  )}
                  {/* Outcome 2 */}
                  <div className={styles.oddsSection}>
                    <div
                      className={`${styles.oddsBox} ${styles.blue} ${match.isSuspended ? styles.suspended : ''}`}
                      onClick={(e) => handleOddsClick(match, 'two', 'back', match.odds?.two?.back?.price || '--', e)}
                    >
                      {match.odds?.two?.back?.price || '--'}
                    </div>
                    <div
                      className={`${styles.oddsBox} ${match.isSuspended ? styles.suspended : ''}`}
                      onClick={(e) => handleOddsClick(match, 'two', 'lay', match.odds?.two?.lay?.price || '--', e)}
                    >
                      {match.odds?.two?.lay?.price || '--'}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default SportsMarket;
